import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, X, Trash, TrashSimple, Copy, MarkdownLogo } from "@phosphor-icons/react";
import { copyText, asMarkdown } from "@/lib/clipboard";

const LEVELS = ["", "beginner", "intermediate", "advanced", "expert"];
const CATEGORIES = ["", "technical", "language", "soft", "tool", "design"];

function copy(text, label = "Copied") { return copyText(text, label); }

export default function SkillsSection() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", level: "", category: "", years: "" });

  const load = async () => {
    const { data } = await api.get("/skills");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return;
    try {
      await api.post("/skills", form);
      setForm({ name: "", level: "", category: "", years: "" });
      setAdding(false);
      load();
    } catch { toast.error("Couldn't save"); }
  };

  const remove = async (id) => {
    await api.delete(`/skills/${id}`);
    load();
  };

  const copyAll = () => {
    const list = items.map((s) => s.level ? `${s.name} (${s.level})` : s.name).join(", ");
    copy(list, "Skills copied");
  };

  const clearAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Delete all ${items.length} skills? This can't be undone.`)) return;
    try {
      const { data } = await api.delete("/vault/clear/skills");
      toast.success(`Wiped ${data.deleted} skills`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear");
    }
  };

  return (
    <section className="card-hard rounded-md bg-white" data-testid="section-skills">
      <div className="flex items-center justify-between p-5 border-b-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-mint)]">
        <div>
          <p className="label-tiny">/skills</p>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Skills</h2>
          <p className="text-xs text-[var(--brand-ink)]/60 mt-1">
            tools, languages, soft skills. tag levels if you want. the extension maps these to job-form skill fields.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono-d text-xs text-[var(--brand-muted)] mr-1">
            {items.length} {items.length === 1 ? "skill" : "skills"}
          </span>
          {items.length > 0 && (
            <>
              <button onClick={copyAll} data-testid="copy-all-skills"
                title="copy all skills as text"
                className="btn-hard bg-white rounded-md px-3 py-2 text-xs flex items-center gap-1.5">
                <Copy size={13}/> <span className="hidden md:inline">copy all</span>
              </button>
              <button onClick={() => copy(asMarkdown.skills(items), "Skills copied as Markdown")}
                data-testid="copy-md-skills"
                title="copy as Markdown"
                className="btn-hard bg-white rounded-md px-2.5 py-2 text-xs flex items-center gap-1.5">
                <MarkdownLogo size={13}/> .md
              </button>
              <button onClick={clearAll} data-testid="clear-all-skills"
                title={`wipe all ${items.length} skills`}
                className="btn-hard bg-[#B91C1C] text-white rounded-md px-2.5 py-2 text-xs flex items-center gap-1.5">
                <TrashSimple size={13} weight="bold"/>
                <span className="hidden sm:inline">delete all</span>
              </button>
            </>
          )}
          <button onClick={() => setAdding(!adding)} data-testid="add-skills"
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-2 text-sm flex items-center gap-1.5 hover:bg-[var(--brand-ink)]">
            <Plus weight="bold" size={14}/> add skill
          </button>
        </div>
      </div>

      <div className="p-5">
        {adding && (
          <form onSubmit={add} className="border-[1.5px] border-[var(--brand-ink)] rounded-md p-4 bg-[var(--pastel-yellow)] mb-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid="editor-skills">
            <div className="md:col-span-4">
              <label className="label-tiny block mb-1.5">Skill *</label>
              <input data-testid="field-skills-name" autoFocus value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Python, French, Public speaking"
                className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
            </div>
            <div className="md:col-span-3">
              <label className="label-tiny block mb-1.5">Level</label>
              <select data-testid="field-skills-level" value={form.level}
                onChange={(e) => setForm({ ...form, level: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white">
                {LEVELS.map((l) => <option key={l} value={l}>{l || "—"}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="label-tiny block mb-1.5">Category</label>
              <select data-testid="field-skills-category" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
              </select>
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button type="submit" data-testid="save-skills"
                className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-3 py-2 text-sm hover:bg-[var(--brand-primary)]">lock it in</button>
              <button type="button" onClick={() => setAdding(false)}
                className="btn-hard bg-white rounded-md p-2"><X size={14}/></button>
            </div>
          </form>
        )}

        {items.length === 0 && !adding ? (
          <p className="text-sm text-[var(--brand-muted)]">no skills yet. add some so the extension can fill skill fields.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="skills-cloud">
            {items.map((s) => (
              <span key={s.id} data-testid={`item-skills-${s.id}`}
                className="border-[1.5px] border-[var(--brand-ink)] rounded-full px-3 py-1 text-sm bg-white flex items-center gap-2 group">
                <span className="font-medium">{s.name}</span>
                {s.level && <span className="text-xs font-mono-d text-[var(--brand-muted)]">{s.level}</span>}
                <button onClick={() => remove(s.id)} data-testid={`delete-skills-${s.id}`}
                  className="opacity-50 hover:opacity-100"><X size={12}/></button>
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
